import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { snagService } from '../../../services/snagService';
import { useLanguage } from '../../../contexts/LanguageContext';

const SnagPhotoGallery = ({ photoPaths }) => {
  const { t } = useLanguage();
  const [photoUrls, setPhotoUrls] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (photoPaths?.length > 0) {
      loadPhotoUrls();
    }
  }, [photoPaths]);

  const loadPhotoUrls = async () => {
    setLoading(true);
    const urls = await Promise.all(
      photoPaths?.map(async (path) => {
        const { url } = await snagService?.getSnagPhotoUrl(path);
        return url;
      })
    );
    setPhotoUrls(urls?.filter(Boolean));
    setLoading(false);
  };

  if (!photoPaths?.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Images" size={18} />
          {t('photos')}
        </h3>
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="ImageOff" size={32} className="mx-auto mb-2" />
          <p className="text-sm">{t('noPhotosAttached')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
        <Icon name="Images" size={18} />
        {t('photos')} ({photoPaths?.length})
      </h3>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={24} className="text-accent animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photoUrls?.map((url, idx) => (
            <button
              key={idx}
              onClick={() => setLightboxIndex(idx)}
              className="relative group overflow-hidden rounded-lg border border-border aspect-square"
            >
              <img
                src={url}
                alt={`${t('snagPhoto')} ${idx + 1}`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <Icon name="ZoomIn" size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setLightboxIndex(null)}
          >
            <Icon name="X" size={24} />
          </button>
          {lightboxIndex > 0 && (
            <button
              className="absolute left-4 text-white hover:text-gray-300"
              onClick={e => { e?.stopPropagation(); setLightboxIndex(prev => prev - 1); }}
            >
              <Icon name="ChevronLeft" size={32} />
            </button>
          )}
          <img
            src={photoUrls?.[lightboxIndex]}
            alt={`${t('snagPhoto')} ${lightboxIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={e => e?.stopPropagation()}
          />
          {lightboxIndex < photoUrls?.length - 1 && (
            <button
              className="absolute right-4 text-white hover:text-gray-300"
              onClick={e => { e?.stopPropagation(); setLightboxIndex(prev => prev + 1); }}
            >
              <Icon name="ChevronRight" size={32} />
            </button>
          )}
          <p className="absolute bottom-4 text-white text-sm">{lightboxIndex + 1} / {photoUrls?.length}</p>
        </div>
      )}
    </div>
  );
};

export default SnagPhotoGallery;
